// Ties a visual field label to the control it sits above (F-023).
//
// Hundreds of forms render a FieldLabel (or an equivalent caption) followed by
// an Input, DropdownSelect, DatePicker, NumericInput or Toggle without wiring
// htmlFor/id by hand, so screen readers announced "edit text" with no purpose
// and required fields were only marked by a red asterisk. FieldLabel now finds
// the control that follows it and links them: the label gets `for`, the
// control gets an id when it has none, and a required label sets
// aria-required on the control.

export const LABELABLE_SELECTOR = [
  'input:not([type="hidden"])',
  'select',
  'textarea',
  'button[role="combobox"]',
  'button[role="switch"]',
  'button[aria-haspopup]',
  '[role="combobox"]',
  '[role="switch"]',
].join(',')

interface ElementLike {
  matches: (selector: string) => boolean
  querySelector: (selector: string) => ElementLike | null
  nextElementSibling: ElementLike | null
  tagName: string
}

/**
 * The control a label describes: the first labelable element in the label's
 * next sibling (or that sibling itself). Only the immediate next sibling is
 * searched, and a sibling that is itself another label stops the search, so a
 * caption above a read-only value never captures an unrelated control further
 * down the form.
 */
export function findLabelledControl<T extends ElementLike>(label: T): T | null {
  const next = label.nextElementSibling as T | null
  if (!next) return null
  if (next.tagName === 'LABEL') return null
  if (next.matches(LABELABLE_SELECTOR)) return next
  return next.querySelector(LABELABLE_SELECTOR) as T | null
}

/** Native label[for] only works on these; anything else is named via aria-labelledby. */
export function isNativelyLabelable(tagName: string): boolean {
  return ['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON', 'METER', 'OUTPUT', 'PROGRESS'].includes(
    tagName.toUpperCase(),
  )
}

interface LinkCandidate {
  tagName: string
  getAttribute: (name: string) => string | null
}

/**
 * How a FieldLabel names its control.
 *
 * - `for`: native label association, for inputs, selects and textareas.
 * - `labelledby`: aria-labelledby only, with no `for`. Used for a combobox
 *   trigger (its fallback aria-label is the placeholder, and aria-labelledby
 *   outranks it), for a switch (a `for` would make a click on the caption
 *   toggle the setting), and for anything that is not natively labelable.
 */
export function labelLinkFor(control: LinkCandidate): 'for' | 'labelledby' {
  const role = control.getAttribute('role')
  if (role === 'switch' || role === 'combobox') return 'labelledby'
  return isNativelyLabelable(control.tagName) ? 'for' : 'labelledby'
}

interface CaptionNode {
  tagName: string
  parentElement: CaptionNode | null
  previousElementSibling: CaptionNode | null
  getAttribute: (name: string) => string | null
  querySelectorAll: (selector: string) => ArrayLike<unknown>
}

/**
 * The plain `<label>` caption drawn just above a control that nobody wired
 * with htmlFor (Q4-BRES7, Q4-BFIN9, Q5-BPHONE2). Many dialogs render
 * `<div><label>Amount</label><NumericInput/></div>`, so the control was
 * announced as an unnamed "edit text". Walks up at most `maxDepth` wrappers
 * from the control, and at each level takes the previous sibling when it is a
 * LABEL with no `for` of its own. The walk stops at the first wrapper that
 * holds a second control, so a caption is never borrowed by a neighbour.
 */
export function findCaptionLabel<T extends CaptionNode>(control: T, maxDepth = 3): T | null {
  let node: T | null = control
  for (let depth = 0; node && depth <= maxDepth; depth++) {
    if (depth > 0 && node.querySelectorAll(LABELABLE_SELECTOR).length > 1) return null
    const prev = node.previousElementSibling as T | null
    if (prev) {
      // A label that wraps its own control (a checkbox row) already names that
      // control and must not be borrowed by the field after it.
      if (
        prev.tagName === 'LABEL' &&
        !prev.getAttribute('for') &&
        prev.querySelectorAll(LABELABLE_SELECTOR).length === 0
      )
        return prev
      return null
    }
    node = node.parentElement as T | null
  }
  return null
}

interface NamedControl extends CaptionNode {
  id: string
  hasAttribute: (name: string) => boolean
  setAttribute: (name: string, value: string) => void
  labels?: ArrayLike<unknown> | null
}

/**
 * Names `control` from its caption label when it has no name yet. Returns
 * true when it linked one. Safe to call on every render: a control that is
 * already named (aria-label, aria-labelledby or a native label) is left alone.
 */
export function linkCaptionLabel(control: NamedControl | null, makeId: () => string): boolean {
  if (!control) return false
  if (control.hasAttribute('aria-label') || control.hasAttribute('aria-labelledby')) return false
  if (control.labels && control.labels.length > 0) return false
  const caption = findCaptionLabel(control) as unknown as
    | (CaptionNode & { id: string; setAttribute: (n: string, v: string) => void })
    | null
  if (!caption) return false
  if (!caption.id) caption.setAttribute('id', makeId())
  control.setAttribute('aria-labelledby', caption.id || caption.getAttribute('id') || '')
  return true
}
